import React, { useEffect, useState } from 'react';
import cx from 'classnames';
import { Form as FinalForm } from 'react-final-form';

import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import type {
  LoginRequest,
  SignupRequest,
} from '@zola-helpers/client/dist/es/redux/auth/authActions';
import { BusinessUnit } from '@zola-helpers/client/dist/es/@types';
import detectBusinessUnit from '@zola-helpers/client/dist/es/util/detectBusinessUnit';

import FormInput from '../../../Form/InputFieldV2/FormInput';
// @ts-expect-error Allow JS import
import ButtonV2 from '../../../Button/ButtonV2';
// @ts-expect-error Allow JS import
import Link from '../../../Link/Link';
import FacebookLoginButton from '../FacebookLoginButton';
import AppleLoginButton, { AppleLoginDataTypes } from '../AppleLoginButton/AppleLoginButton';

import useAuthErrorHandler from '../../../../hooks/useAuthErrorHandler';
import useGuessTimezone from '../../../../hooks/useGuessTimezone';
import * as Validations from '../../../Form/util/validations';
import { login, signup } from '../../../../actions/AuthActions';

import {
  AuthState,
  MinimalAuthFormType,
  MinimalAuthFormProps,
  MinimalAuthFormValues,
} from '../../types';

import styles from './MinimalAuthForm.module.less';
import containerStyles from '../../AuthForms.module.less';
import { LinkV2 } from '../../../LinkV2';
import useRecaptchaV3, { ZolaRecaptchaV3Actions } from '../../../../hooks/useRecaptchaV3';
import ZolaTermsPrivacy from '../ZolaTermsPrivacy';
import { usePrevious } from '../../../../hooks/usePrevious';

// For backwards compatibility with clients
export type { MinimalAuthFormProps };

export const MinimalAuthForm: React.FC<MinimalAuthFormProps> = ({
  authState,
  setAuthState,
  onAny200Login,
  signupCallback,
  onClickSignup,
  onClickLogin,
  passwordInputType,
  onTogglePasswordVisibility,
  authHed,
  loginCTA = 'Log in',
  signupCTA = 'Sign up for free',
  includeNameInput = false,
  useRefactoredRecaptcha,
  initialMinimalAuthFormState,
  isMidInquiryFlowAuth = false,
  initialValues: initialValuesFromProps = {},
  submitDisabled = false,
}) => {
  const [signupFormType, setSignupFormType] = useState<MinimalAuthFormType>(
    initialMinimalAuthFormState ||
      (authState === AuthState.SIGNUP
        ? MinimalAuthFormType.SOCIAL_SIGNUP
        : MinimalAuthFormType.LOGIN)
  );
  const [errorMessage, handleError, preventSubmit] = useAuthErrorHandler();
  const [timezone] = useGuessTimezone();
  const businessUnit = detectBusinessUnit();
  const firstInitialValueState =
    authState === AuthState.SIGNUP
      ? { ...initialValuesFromProps, timezone }
      : initialValuesFromProps;
  const [initialValues, setInitialValues] =
    useState<Partial<MinimalAuthFormValues>>(firstInitialValueState);
  const isLogin = authState === AuthState.LOGIN;

  const [email, setEmail] = useState<string>();
  const [password, setPassword] = useState<string>();

  const prevAuthState = usePrevious(authState);
  useEffect(() => {
    const formTypeChanged = prevAuthState !== undefined && prevAuthState !== authState;
    if (formTypeChanged) {
      const isSignup = authState === AuthState.SIGNUP;
      setInitialValues({ email, password, ...(isSignup ? { timezone } : {}) });
    }
  }, [authState, email, password, prevAuthState, timezone]);

  const extractFirstLastName = (
    values: MinimalAuthFormValues
    // TODO: remove business_unit_source from SignupRequest in zola-helpers
  ): Omit<SignupRequest, 'business_unit_source'> => {
    // this function is called post-validation so it should not ever fail
    const { fullName, firstName, lastName, ...rest } = values;

    if (fullName) {
      const matches = fullName.trim().match(Validations.FIRST_AND_LAST_NAME_REGEX);
      if (matches !== null) {
        const [, parsedFirstName, parsedLastName] = matches;
        return {
          ...rest,
          first_name: parsedFirstName.trim(),
          last_name: parsedLastName.trim(),
        };
      }
    }
    return {
      ...rest,

      // These aren't form values, but they can be passed through as initial values
      // so if we've captured the user's name in a step before the auth modal
      // is shown, we can pass those names through to the create user request
      //
      // see https://newamsterdamlabs.atlassian.net/browse/INSPO-8859
      first_name: firstName?.trim() || initialValuesFromProps.firstName?.trim(),
      last_name: lastName?.trim() || initialValuesFromProps.lastName?.trim(),
    };
  };
  const { executeRecaptcha } = !useRefactoredRecaptcha
    ? // eslint-disable-next-line react-hooks/rules-of-hooks
      useGoogleReCaptcha()
    : { executeRecaptcha: undefined };
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const refactoredGetToken = useRefactoredRecaptcha ? useRecaptchaV3() : undefined;

  let getRecaptchaToken = (actionName: string): Promise<string> => {
    if (!executeRecaptcha) {
      // eslint-disable-next-line no-console
      console.log('Execute recaptcha not yet available');
      return Promise.resolve('');
    }

    return executeRecaptcha(actionName);
  };

  if (useRefactoredRecaptcha) {
    getRecaptchaToken = refactoredGetToken as (action?: string | undefined) => Promise<string>;
  }

  const onLoginSubmit = (values: LoginRequest): Promise<void> => {
    return getRecaptchaToken(ZolaRecaptchaV3Actions.LOGIN)
      .then((token) => {
        return login({ ...values, captchaV3Token: token })
          .then((data) => onAny200Login(data, values.email))
          .catch(handleError);
      })
      .catch(handleError);
  };

  const onSignupSubmit = (values: MinimalAuthFormValues) => {
    signup(extractFirstLastName(values)).then(signupCallback).catch(handleError);
  };

  const renderTitle = () => {
    if (authHed) return authHed;
    switch (authState) {
      case AuthState.SIGNUP: {
        return businessUnit === BusinessUnit.MARKETPLACE ? 'Sign Up' : 'Create Account';
      }
      case AuthState.LOGIN:
      default:
        return 'Log In';
    }
  };

  const ctaText = isLogin ? loginCTA : signupCTA;

  const formContent = (submitting: boolean) => (
    <>
      {!isLogin && !isMidInquiryFlowAuth ? (
        <LinkV2
          className={styles.signUpWithSocialLink}
          onClick={() => setSignupFormType(MinimalAuthFormType.SOCIAL_SIGNUP)}
          arrow
          arrowPosition="prefix"
        >
          Use Facebook or Apple
        </LinkV2>
      ) : null}

      <div className={styles.formInputsContainer}>
        {!isLogin && includeNameInput && (
          <FormInput
            className={containerStyles.field}
            name="fullName"
            type="text"
            label="Name"
            placeholder="Name"
            id="zui-auth__fullName"
            isRequired
            validate={[Validations.REQUIRED, Validations.CONTAINS_FIRST_AND_LAST_NAME]}
          />
        )}
        <FormInput
          className={containerStyles.field}
          name="email"
          type="email"
          label="Email"
          placeholder="Email"
          id="zui-auth__email"
          isRequired
          inputOnChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
          validate={[Validations.REQUIRED, Validations.EMAIL]}
        />
        <div className={styles.relativeContainer}>
          <FormInput
            className={cx(containerStyles.field, styles.password)}
            name="password"
            id="zui-auth__password"
            label="Password"
            type={passwordInputType}
            placeholder="Password (8 or more characters)"
            isRequired
            password
            inputOnChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
            validate={[Validations.REQUIRED, Validations.PASSWORD]}
            onTogglePasswordVisibility={onTogglePasswordVisibility}
          />
          {isLogin && (
            <Link
              arrow={false}
              role="button"
              onClick={() => setAuthState(AuthState.FORGOT)}
              className={styles.topRight}
            >
              Forgot Password?
            </Link>
          )}
        </div>

        <ButtonV2
          disabled={submitting || preventSubmit || submitDisabled}
          fullWidth
          type="submit"
          className={cx(`${authState}-submit`, styles.ctaText)}
        >
          {ctaText}
        </ButtonV2>
      </div>
    </>
  );

  const socialLoginButtons = (
    <>
      <div className={cx(styles.externalLoginContainer, !isLogin && styles.flexColumns)}>
        <FacebookLoginButton
          buttonText={isLogin ? 'Log in with Facebook' : 'Sign up with Facebook'}
          callback={onAny200Login}
          errorCallback={handleError}
        />
        <AppleLoginButton
          authState={isLogin ? AppleLoginDataTypes.SIGNIN : AppleLoginDataTypes.SIGNUP}
          callback={onAny200Login}
          errorCallback={handleError}
        />
      </div>

      {!isLogin && signupFormType === MinimalAuthFormType.SOCIAL_SIGNUP && (
        <div className={styles.signUpWithEmailLink}>
          Or{' '}
          <LinkV2
            onClick={() => setSignupFormType(MinimalAuthFormType.EMAIL_SIGNUP)}
            data-testid="signup-with-email"
          >
            Sign up with email instead
          </LinkV2>
        </div>
      )}
    </>
  );

  return (
    <FinalForm<MinimalAuthFormValues>
      onSubmit={
        isLogin
          ? (values: MinimalAuthFormValues) => onLoginSubmit(values)
          : (values: MinimalAuthFormValues) => onSignupSubmit(values)
      }
      initialValues={initialValues}
    >
      {({ handleSubmit, submitting }) => (
        <>
          {!isMidInquiryFlowAuth && (
            <div role="heading" aria-level={1} className={styles.title}>
              {renderTitle()}
            </div>
          )}

          {isLogin ? <hr className={styles.hr} /> : null}

          <div className={styles.relativeContainer}>
            <form
              id={`auth_${authState.toLowerCase()}`}
              data-testid={`auth_${authState.toLowerCase()}`}
              onSubmit={handleSubmit}
            >
              {isLogin || signupFormType === MinimalAuthFormType.EMAIL_SIGNUP
                ? formContent(submitting)
                : socialLoginButtons}
            </form>
            {isLogin && <div className={styles.other} />}

            {isLogin ? socialLoginButtons : null}

            {!isLogin && !isMidInquiryFlowAuth && <ZolaTermsPrivacy />}
            {errorMessage && <div className={containerStyles.error}>{errorMessage}</div>}
            {isLogin && (
              <div className={styles.footer}>
                <div className={styles.footerText}>
                  Don&#39;t have an account?{' '}
                  <Link id="zui-auth__signUpBtn" role="button" onClick={onClickSignup}>
                    {businessUnit === BusinessUnit.STORE ? 'Create account now' : 'Sign up now'}
                  </Link>
                </div>
              </div>
            )}
          </div>

          {!isLogin && (
            <>
              {!isMidInquiryFlowAuth && <hr className={styles.footerHr} />}
              <div
                className={cx(styles.footer, styles.alreadyHaveAccountFooter, {
                  [styles.inquiryFlowAlreadyHaveAccountFooter]: isMidInquiryFlowAuth,
                })}
              >
                <div className={styles.footerText}>
                  Already have a Zola account?{' '}
                  <Link id="zui-auth__signInBtn" role="button" onClick={onClickLogin}>
                    Sign in here
                  </Link>
                </div>
              </div>
            </>
          )}
          {!isLogin && isMidInquiryFlowAuth && (
            <ZolaTermsPrivacy isMidInquiryFlowAuth={isMidInquiryFlowAuth} />
          )}
        </>
      )}
    </FinalForm>
  );
};

export default MinimalAuthForm;
